import { customElement, query } from 'lit/decorators.js';
import { html, unsafeCSS } from 'lit';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTISPAM } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  ExchangeAntispamKeywords,
  ExchangeProtectionAntispamFormValue,
} from '../../../../utils/uiSettings/exchange/exchange-protection.types';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { ExchangeAntispamRule, ModuleId, RblServerItem, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import {
  getExchangeAntispamDefaultValue,
  getExchangeAntispamSearchableKeywords,
} from '../../../../utils/uiSettings/exchange/exchange-protection.values';
import _ from 'lodash';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { FontSizes } from 'apps/webcomponents/src/types/common/types';
import { FontWeights } from 'apps/webcomponents/src/types/common/types';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzInputNumberWebComponent, InputWidthValues } from 'apps/webcomponents/src/ui/gzInput';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { GzExchangeAntispamSettingsTableWebComponent } from '../gridComponents/settings/GzExchangeAntispamSettingsTableWebComponent';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../ui/gzChipsGeneratorInput';
import { GzExchangeAntispamRulesGridWebComponent } from '../gridComponents/filtering/GzExchangeAntispamRulesGridWebComponent';
import { MinValidator } from 'apps/webcomponents/src/ui/gzForm/validators/min.validator';
import { MaxValidator } from '../../../../../../../ui/gzForm/validators/max.validator';
import { validateEmailPattern } from '../../helpers/utils/validators';
import { HostnameValidator } from '../../../../../../../ui/gzForm/validators/hostname.validator';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

/**
 * @summary Type here the description of this component
 * @tag GzExchangeAntiSpamWebComponent
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTISPAM)
export class GzExchangeAntispamWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ExchangeProtectionAntispamFormValue,
  ExchangeAntispamKeywords
> {
  protected searchableKeywords = getExchangeAntispamSearchableKeywords();
  protected path = { moduleId: ModuleId.EXCHANGE_PROTECTION, sectionId: SectionId.EXCHANGE_ANTISPAM };

  @query('#exchange-antispam-toggle')
  private antispamToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#exchange-antispam-info-text')
  private antispamInfoText: GzTextWebComponent;
  @query('#exchange-antispam-content')
  private liveSearchContent: HTMLElement;
  @query('#dns-ip-address-form')
  private dnsIpAddress: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#dns-query-timeout-form')
  private dnsQueryTimeout: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#servers-form')
  private serversForm: GzFormFieldWebComponent<GzExchangeAntispamSettingsTableWebComponent, RblServerItem[]>;
  @query('#exchange-antispam--whitelist-toggle')
  private whiteListToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#email-whitelist')
  private emailWhiteList: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent, string[]>;
  @query('#exchange-antispam-settings-table')
  private exchangeAntispamSettingsTable: GzExchangeAntispamSettingsTableWebComponent;
  @query('#exchange-antispam-filtering-grid')
  private exchangeAntispamFilteringGrid: GzFormFieldWebComponent<
    GzExchangeAntispamRulesGridWebComponent,
    ExchangeAntispamRule[]
  >;

  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }
  protected createFormGroup(): void {
    this.dnsQueryTimeout.Validators = [
      MinValidator(1, translateFn('DNS query timeout must be greater than 0.')),
      MaxValidator(30000, translateFn('DNS query timeout must be less than 30000.')),
    ];
    this.dnsIpAddress.Validators = [HostnameValidator(translateFn('Invalid host address'), true)];

    this.formGroup = FormGroup.build({
      uiSettings: {
        exchange: {
          antispam: {
            enable: this.antispamToggle,
            settings: {
              rblDnsAddress: this.dnsIpAddress,
              rblDnsMsTimeout: this.dnsQueryTimeout,
              rblServers: this.serversForm,
            },
            whiteList: {
              enable: this.whiteListToggle,
              list: this.emailWhiteList,
            },
            rules: this.exchangeAntispamFilteringGrid,
          },
        },
      },
    });
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);
    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'exchange', 'antispam']);
    }
    return value;
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    this.formGroup.value = {
      uiSettings: {
        exchange: {
          antispam: this.valueT.uiSettings.exchange.antispam,
        },
      },
    };
  }

  protected getValueFromManager(): ExchangeProtectionAntispamFormValue {
    const defaultValue = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: {
          [SectionId.EXCHANGE_ANTISPAM]: getExchangeAntispamDefaultValue(),
        },
      },
    };
    const value = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: {
          [SectionId.EXCHANGE_ANTISPAM]: PolicyPanelManager.getInstance().getPolicy('uiSettings.exchange.antispam'),
        },
      },
    };

    return deepmerge(defaultValue, value);
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="exchange-antispam-info-text" fontSize="12" fontWeight="400" class="infoText">
      ${translateFn(
        'The Antispam module provides multi-layered protection against spam and phishing. Emails are filtered using prioritized rules for sender and recipient groups, with customizable actions applied to detected threats.'
      )}</gz-text
    >`;
  }

  protected renderContent() {
    return html`
      <div id="exchange-antispam-content" class="content">
        ${this.renderAntispamFiltering()} ${this.renderSettings()} ${this.renderWhiteList()}
      </div>
    `;
  }

  private renderAntispamFiltering() {
    return html`<div>
      <gz-title
        class="mt-4 module-subtitle"
        searchable-label="${this.searchableKeywords[ExchangeAntispamKeywords.AntispamFiltering].label}"
      >
        ${this.searchableKeywords[ExchangeAntispamKeywords.AntispamFiltering].translatedLabel}
      </gz-title>
      <gz-form-field id="exchange-antispam-filtering-grid">
        <gz-policy-panel-exchange-antispam-rules-grid
          gz-form-input
          class="removeGridPadding"
        ></gz-policy-panel-exchange-antispam-rules-grid>
      </gz-form-field>
    </div>`;
  }

  private renderSettings() {
    return html`<div>
      <gz-title
        class="mt-4 module-subtitle"
        searchable-label="${this.searchableKeywords[ExchangeAntispamKeywords.Settings].label}"
      >
        ${this.searchableKeywords[ExchangeAntispamKeywords.Settings].translatedLabel}
      </gz-title>
      <gz-title
        class="form-subtitle"
        searchable-label="${this.searchableKeywords[ExchangeAntispamKeywords.RblSettings].label}"
        fontWeight="${FontWeights.SEMIBOLD}"
        fontSize="${FontSizes.PARAGRAPH_11}"
      >
        ${this.searchableKeywords[ExchangeAntispamKeywords.RblSettings].translatedLabel}
      </gz-title>
      <gz-text
        id="rbl-settings-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="${FontWeights.NORMAL}"
        class="infoText mt-1"
        >${translateFn(
          'The Real-time Blackhole List (RBL) module checks sender email servers to assess whether they have a known spammer reputation. This process involves a DNS lookup, requiring you to specify a DNS server to perform the query.'
        )}</gz-text
      >
      <gz-form-field
        id="dns-ip-address-form"
        class="label-mw-250 mt-2"
        label="${this.searchableKeywords[ExchangeAntispamKeywords.DnsIpAddress].translatedLabel}"
        searchable-label="${this.searchableKeywords[ExchangeAntispamKeywords.DnsIpAddress].label}"
      >
        <gz-input
          gz-form-input
          inputWidth="${InputWidthValues.LARGE}"
          placeholder="${translateFn('Enter an address')}"
        ></gz-input>
      </gz-form-field>
      <gz-form-field
        id="dns-query-timeout-form"
        class="label-mw-250"
        label="${this.searchableKeywords[ExchangeAntispamKeywords.DnsQueryTimeout].translatedLabel}"
        searchable-label="${this.searchableKeywords[ExchangeAntispamKeywords.DnsQueryTimeout].label}"
      >
        <gz-input-number
          gz-form-input
          inputWidth="${InputWidthValues.LARGE}"
          placeholder="${translateFn('DNS query timeout')}"
          min="1"
          max="30000"
        ></gz-input-number>
      </gz-form-field>
      <gz-form-field id="servers-form">
        <gz-policy-panel-exchange-antispam-settings-table
          gz-form-input
          id="exchange-antispam-settings-table"
          class="removeGridPadding"
        >
        </gz-policy-panel-exchange-antispam-settings-table>
      </gz-form-field>
    </div>`;
  }
  protected renderWhiteList() {
    return html`<gz-form-field id="exchange-antispam--whitelist-toggle" class="mt-4 mbw-1">
        <gz-toggle
          class="toggle-subtitle"
          searchable-label="${this.searchableKeywords[ExchangeAntispamKeywords.WhiteList].label}"
          gz-form-input
          @toggleChange="${this.onToggleWhiteListChange}"
        >
          <span>${this.searchableKeywords[ExchangeAntispamKeywords.WhiteList].translatedLabel}</span>
        </gz-toggle>
      </gz-form-field>
      <gz-text id="rbl-emails-domains-text" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
        >${translateFn(
          'Add known email addresses and domains to lists of trusted or untrusted senders to prevent unnecessary server consumption.'
        )}</gz-text
      >
      <gz-form-field id="email-whitelist" class="label-mw-250 mt-2 hidden ">
        <gz-chips-generator-input
          gz-form-input
          hasFloatingLabel
          .validators=${[validateEmailPattern]}
          .strings="${{
            label: translateFn('Email addresses or domains'),
            placeholder: translateFn('Enter email addresses or domain patterns'),
            hintText: translateFn('Use a comma to separate multiple entries.'),
            clearListButtonText: translateFn('Clear list'),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Remove'),
            deleteWindowTitle: translateFn('Remove email addresses and domains?'),
            noSearchResultsString: translateFn('No results found'),
            deleteWindowContentText: translateFn(
              'You are about to remove all email addresses and domains from the list. This action cannot be undone.'
            ),
            notUniqueErrorMessage: translateFn('The entry already exists.'),
          }}"
        ></gz-chips-generator-input>
      </gz-form-field>`;
  }

  protected renderTitle() {
    return html`<gz-form-field id="exchange-antispam-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[ExchangeAntispamKeywords.Antispam].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[ExchangeAntispamKeywords.Antispam].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.antispamInfoText, !event.detail.checked);
    toggleDisabled(this.liveSearchContent, !event.detail.checked);
  }
  onToggleWhiteListChange(event: CustomEvent) {
    toggleVisibility(this.emailWhiteList, event.detail.checked);
  }

  public reset() {
    super.reset();
    this.exchangeAntispamFilteringGrid.Input.reset();
    this.serversForm.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTI_SPAM: GzExchangeAntispamWebComponent;
  }
}
